import { useState } from 'react'
import { Alert, Button, Code, CopyButton, Group, Modal, Stack, Text } from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { IconKey } from '@tabler/icons-react'
import type { UserDto } from '@ledokol/contracts'
import { resetUserPassword } from '../../shared/api/users'
import { isApiError } from '../../shared/api/client'

/**
 * Кнопка «Сбросить пароль» в карточке сотрудника. Временный пароль живёт только в state
 * модалки и стирается при закрытии: повторно его не показать (сервер хранит лишь хеш).
 * Видимость (право users.reset_password, не себе, не стабу) решает вызывающий.
 */
export function ResetPasswordSection({ user, onReset }: { user: UserDto; onReset: () => void }) {
  const [confirming, setConfirming] = useState(false)
  const [loading, setLoading] = useState(false)
  const [temporary, setTemporary] = useState<string | null>(null)

  async function doReset() {
    setLoading(true)
    try {
      setTemporary(await resetUserPassword(user.id))
      setConfirming(false)
      onReset()
    } catch (e) {
      const message = isApiError(e) ? e.response?.data.error.message : undefined
      notifications.show({ message: message ?? 'Не удалось сбросить пароль', color: 'red' })
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      <Button variant="light" color="orange" leftSection={<IconKey size={16} />} onClick={() => setConfirming(true)}>
        Сбросить пароль
      </Button>

      <Modal opened={confirming} onClose={() => setConfirming(false)} title={`Сбросить пароль «${user.full_name}»?`} centered>
        <Stack>
          <Text size="sm">
            Будет выдан временный пароль. Все сессии сотрудника завершатся, при входе он будет обязан сменить пароль.
          </Text>
          <Group justify="flex-end">
            <Button variant="subtle" onClick={() => setConfirming(false)}>Отмена</Button>
            <Button color="orange" loading={loading} onClick={doReset}>Да, сбросить</Button>
          </Group>
        </Stack>
      </Modal>

      <Modal
        opened={temporary !== null}
        onClose={() => setTemporary(null)}
        title="Временный пароль"
        centered
        closeOnClickOutside={false}
      >
        {temporary && (
          <Stack>
            <Alert color="yellow">
              Пароль показывается один раз. Передайте его сотруднику лично или в защищённом канале.
            </Alert>
            <Code block data-testid="temporary-password" style={{ fontSize: 18, letterSpacing: 1 }}>
              {temporary}
            </Code>
            <Group justify="space-between">
              <CopyButton value={temporary}>
                {({ copied, copy }) => (
                  <Button variant="light" color={copied ? 'teal' : 'blue'} onClick={copy}>
                    {copied ? 'Скопировано' : 'Копировать'}
                  </Button>
                )}
              </CopyButton>
              <Button onClick={() => setTemporary(null)}>Готово</Button>
            </Group>
          </Stack>
        )}
      </Modal>
    </>
  )
}
